import React, { useState, useEffect, useCallback } from 'react';
import AdDetailModal from '../components/AdDetailModal';
import axios from 'axios';
import { useTheme } from '../ThemeContext';
import { useLanguage } from '../LanguageContext';
import useIsMobile from '../components/UnitsDeals/useIsMobile';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

const GREEN = '#22c55e';
const RED = '#ef4444';
const BLUE = '#3b82f6';
const PURPLE = '#8b5cf6';
const CYAN = '#06b6d4';

const THEMES = {
  dark: {
    accent: '#FFFC00', bg: '#0b0d0f', card: '#141619', border: '#1f2228',
    text: '#e8e4dc', dim: '#8a8578', headerBg: '#101214', navBg: '#101214',
    inputBg: '#1a1d21', rowAlt: 'rgba(255,255,255,0.015)',
    activeTagBg: 'rgba(34,197,94,0.15)', pausedTagBg: 'rgba(139,143,163,0.15)',
    adsetBg: 'rgba(255,252,0,0.02)', expandedBg: 'rgba(255,252,0,0.02)',
    fieldBg: 'rgba(0,0,0,0.2)', kpiBorder: 'rgba(255,252,0,0.3)',
    formBg: 'rgba(255,252,0,0.03)', formTagBg: 'rgba(255,252,0,0.1)',
    toastOkBg: 'rgba(255,252,0,0.12)', toastOkBorder: 'rgba(255,252,0,0.3)',
    toastErrBg: 'rgba(239,68,68,0.15)', toastErrBorder: 'rgba(239,68,68,0.3)',
    activeFilterBg: '#FFFC00', activeFilterColor: '#000',
    modeBg: 'rgba(255,252,0,0.1)', modeLifetimeBg: 'rgba(139,143,163,0.1)',
    objTagBg: 'rgba(99,102,241,0.15)', objTagColor: '#818cf8',
    gridStroke: '#1f2228', tooltipBg: '#1a1d21',
  },
  light: {
    accent: '#d4a800', bg: '#f5f5f0', card: '#ffffff', border: '#e0ddd5',
    text: '#1a1a1a', dim: '#6b6b6b', headerBg: '#ffffff', navBg: '#fafaf7',
    inputBg: '#f0efe9', rowAlt: 'rgba(0,0,0,0.025)',
    activeTagBg: 'rgba(34,197,94,0.1)', pausedTagBg: 'rgba(0,0,0,0.06)',
    adsetBg: 'rgba(180,150,0,0.03)', expandedBg: 'rgba(180,150,0,0.03)',
    fieldBg: 'rgba(0,0,0,0.04)', kpiBorder: 'rgba(180,150,0,0.3)',
    formBg: 'rgba(180,150,0,0.04)', formTagBg: 'rgba(180,150,0,0.12)',
    toastOkBg: 'rgba(180,150,0,0.1)', toastOkBorder: 'rgba(180,150,0,0.3)',
    toastErrBg: 'rgba(220,38,38,0.1)', toastErrBorder: 'rgba(220,38,38,0.3)',
    activeFilterBg: '#d4a800', activeFilterColor: '#fff',
    modeBg: 'rgba(180,150,0,0.1)', modeLifetimeBg: 'rgba(0,0,0,0.05)',
    objTagBg: 'rgba(99,102,241,0.1)', objTagColor: '#6366f1',
    gridStroke: '#e8e5dd', tooltipBg: '#ffffff',
  },
};

const PALETTE = ['#FFFC00', BLUE, GREEN, PURPLE, '#f97316', CYAN, RED, '#ec4899'];

function getS(t, isMobile) {
  const px = isMobile ? 14 : 32;
  return {
    page: { fontFamily: "'Tajawal', sans-serif", background: t.bg, color: t.text, minHeight: '100vh', transition: 'background 0.3s, color 0.3s' },
    header: { background: t.headerBg, borderBottom: `1px solid ${t.border}`, padding: `16px ${px}px`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, transition: 'background 0.3s' },
    content: { padding: `24px ${px}px`, maxWidth: 1400, margin: '0 auto' },
    card: { background: t.card, border: `1px solid ${t.border}`, borderRadius: 14, padding: 24, marginBottom: 16, transition: 'background 0.3s' },
    btn: { background: t.accent, color: '#000', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif", fontSize: '0.85rem' },
    btnOutline: { background: 'transparent', color: t.accent, border: `1px solid ${t.accent}`, borderRadius: 8, padding: '8px 20px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif", fontSize: '0.85rem' },
    btnSmall: { background: 'transparent', color: t.accent, border: `1px solid ${t.border}`, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif", fontSize: '0.78rem' },
    th: { textAlign: 'right', padding: '10px 14px', color: t.dim, fontWeight: 600, fontSize: '0.73rem', borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.5 },
    td: { padding: '10px 14px', borderBottom: `1px solid ${t.border}`, fontSize: '0.85rem' },
    kpi: { textAlign: 'center', padding: '20px 12px', background: t.card, border: `1px solid ${t.border}`, borderRadius: 14 },
    tag: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 600, marginLeft: 4 },
    tooltip: { backgroundColor: t.tooltipBg, border: `1px solid ${t.border}`, borderRadius: 10, color: t.text, fontSize: '0.8rem', fontFamily: "'Tajawal',sans-serif", boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
  };
}

const fmt = n => n != null && !isNaN(n) ? Number(n).toLocaleString('en-US') : '—';
const fmtSAR = n => n != null && !isNaN(n) ? Number(n).toFixed(2) + ' SAR' : '—';
const fmtP = n => n != null && !isNaN(n) ? Number(n).toFixed(2) + '%' : '—';
const fmtK = n => { if (n == null || isNaN(n)) return '—'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(1)+'K'; return fmt(n); };

function StatusTag({ status, t, S }) {
  const active = status === 'ACTIVE';
  return <span style={{ ...S.tag, background: active ? t.activeTagBg : t.pausedTagBg, color: active ? GREEN : t.dim }}>
    {active ? 'Active' : status === 'PAUSED' ? 'Paused' : status || '—'}
  </span>;
}

function ChartTooltip({ active, payload, label, S }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={S?.tooltip || {}}>
      <div style={{ padding: '10px 14px' }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
            <span style={{ color: p.color }}>{p.name}</span>
            <span style={{ fontWeight: 600 }}>{typeof p.value === 'number' ? p.value.toLocaleString('en-US', { maximumFractionDigits: 2 }) : p.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function getLocalDate(daysAgo = 0) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

const PRESET_KEYS = ['today', 'yesterday', '3d', '7d', '30d', 'lifetime', 'custom'];

function SnapAds() {
  const { isDark } = useTheme();
  const { t: tr, isRTL } = useLanguage();
  const isMobile = useIsMobile();
  const t = isDark ? THEMES.dark : THEMES.light;
  const S = getS(t, isMobile);
  const kpiCols = isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)';
  const kpiFont = isMobile ? '1.5rem' : '2.2rem';
  const kpiFont2 = isMobile ? '1.25rem' : '1.6rem';
  const dateInputStyle = { background: t.inputBg, border: `1px solid ${t.border}`, borderRadius: 8, padding: '7px 12px', color: t.text, fontFamily: "'Tajawal',sans-serif", fontSize: '0.85rem' };

  const [activePreset, setActivePreset] = useState('yesterday');
  const [customFrom, setCustomFrom] = useState(getLocalDate(30));
  const [customTo, setCustomTo] = useState(getLocalDate(0));
  const [data, setData] = useState({ campaigns: [], fetchDate: null });
  const [dailyTrend, setDailyTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [creativePreview, setCreativePreview] = useState(null);
  const [sortBy, setSortBy] = useState('spend');
  const [statusF, setStatusF] = useState('all');
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      let params = {};
      const today = getLocalDate(0);
      switch (activePreset) {
        case 'lifetime': break;
        case 'today': params = { date_from: today, date_to: today }; break;
        case 'yesterday': { const y = getLocalDate(1); params = { date_from: y, date_to: y }; break; }
        case '3d': params = { date_from: getLocalDate(3), date_to: today }; break;
        case '7d': params = { date_from: getLocalDate(7), date_to: today }; break;
        case '30d': params = { date_from: getLocalDate(30), date_to: today }; break;
        case 'custom': params = { date_from: customFrom, date_to: customTo }; break;
        default: break;
      }
      const [overviewRes, historyRes] = await Promise.all([
        axios.get('/api/snap/overview', { params }),
        axios.get('/api/snap/history/summary'),
      ]);
      setData(overviewRes.data);
      setDailyTrend(historyRes.data.daily_trend || []);
    } catch (e) { console.error(e); setError(true); }
    setLoading(false);
  }, [activePreset, customFrom, customTo]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post('/api/snap/sync');
      setToast({ msg: tr('snapAdsSyncStarted'), type: 'ok' });
      setTimeout(() => loadData(), 10000);
    } catch (e) {
      setToast({ msg: e.response?.data?.error || 'Sync failed', type: 'err' });
    }
    setSyncing(false);
    setTimeout(() => setToast(null), 5000);
  };

  // Compute totals
  const totals = { impressions: 0, swipes: 0, spend: 0, conversions: 0, reach: 0, video_views: 0 };
  const campaignsWithSpend = [];
  data.campaigns.forEach(c => {
    const m = c.metrics || {};
    totals.impressions += m.impressions || 0;
    totals.swipes += m.swipes || 0;
    totals.spend += m.spend || 0;
    totals.conversions += m.conversions || 0;
    totals.reach += m.reach || 0;
    totals.video_views += m.video_views || 0;
    if (m.spend > 0) campaignsWithSpend.push(c);
  });
  totals.ctr = totals.impressions > 0 ? (totals.swipes / totals.impressions) * 100 : 0;
  totals.cpm = totals.impressions > 0 ? (totals.spend / totals.impressions) * 1000 : 0;
  totals.cpc = totals.swipes > 0 ? totals.spend / totals.swipes : 0;
  // Exact de-duped reach + frequency from the server (Snap has no summable reach field).
  const accountReach = data.reachExact ? (data.accountReach || 0) : null;
  const accountFreq = data.reachExact ? (data.accountFrequency || 0) : null;

  // Spend pie data
  const spendPie = campaignsWithSpend.sort((a,b) => b.metrics.spend - a.metrics.spend).map((c, i) => ({
    name: c.name?.length > 20 ? c.name.slice(0, 20) + '...' : c.name,
    value: Math.round(c.metrics.spend * 100) / 100,
    color: (isDark ? PALETTE : ['#d4a800', '#2563eb', '#16a34a', '#7c3aed', '#ea580c', '#0891b2', '#dc2626', '#db2777'])[i % 8],
  }));

  // Daily trend for charts
  const trendData = dailyTrend.map(d => ({
    date: typeof d.date === 'string' ? d.date.slice(5) : d.date,
    spend: Math.round(d.spend * 100) / 100,
    impressions: d.impressions,
    swipes: d.swipes,
  }));

  // Expanded campaign detail
  const selectedCampaign = expandedCampaign ? data.campaigns.find(c => c.campaign_id === expandedCampaign) : null;

  const TT = (props) => <ChartTooltip {...props} S={S} />;

  return (
    <div style={{ ...S.page, direction: isRTL ? 'rtl' : 'ltr' }}>
      <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap" rel="stylesheet" />

      {toast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
          padding: '12px 24px', borderRadius: 10, fontWeight: 600, fontSize: '0.9rem', fontFamily: "'Tajawal',sans-serif",
          background: toast.type === 'err' ? t.toastErrBg : t.toastOkBg,
          border: `1px solid ${toast.type === 'err' ? t.toastErrBorder : t.toastOkBorder}`,
          color: toast.type === 'err' ? RED : t.accent,
        }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: '1.2rem', fontWeight: 800, color: t.accent }}>Snapchat Ads</span>
          <span style={{ fontSize: '0.8rem', color: t.dim }}>
            {data.campaigns.length} {tr('snapAdsCampaigns')} | {data.mode === 'lifetime' ? tr('snapAdsAllTime') : data.fetchDate}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btn} onClick={handleSync} disabled={syncing}>{syncing ? tr('syncing') : tr('snapAdsSyncNow')}</button>
          <button style={S.btnOutline} onClick={loadData} disabled={loading}>{tr('snapAdsRefresh')}</button>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={{ background: t.navBg, borderBottom: `1px solid ${t.border}`, padding: `12px ${isMobile ? 14 : 32}px`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', transition: 'background 0.3s' }}>
        {PRESET_KEYS.map(key => {
          const labels = { today: tr('execToday') || 'Today', lifetime: tr('snapAdsAllTime'), yesterday: tr('execYesterday'), '3d': tr('execLast3'), '7d': tr('snapAdsLast7'), '30d': tr('snapAdsLast30'), custom: tr('snapAdsCustom') };
          return (
            <button key={key} onClick={() => setActivePreset(key)}
              style={{
                ...S.btnSmall,
                background: activePreset === key ? t.activeFilterBg : 'transparent',
                color: activePreset === key ? t.activeFilterColor : t.accent,
                borderColor: activePreset === key ? t.accent : t.border,
              }}>
              {labels[key]}
            </button>
          );
        })}
        {activePreset === 'custom' && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={dateInputStyle} />
            <span style={{ color: t.dim }}>{tr('to')}</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={dateInputStyle} />
          </>
        )}
      </div>

      <div style={S.content}>
        {data.note && (
          <div style={{ background: t.modeBg, border: `1px solid ${t.accent}40`, borderRadius: 10, padding: '12px 18px', marginBottom: 16, fontSize: '0.82rem', color: t.accent }}>
            {data.note}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: t.dim, fontSize: '1.1rem' }}>{tr('snapAdsLoading')}</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 64, color: t.dim }}>
            <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>&#9888;&#65039;</div>
            <div style={{ marginBottom: 12 }}>{isRTL ? 'تعذّر تحميل بيانات سناب شات' : 'Could not load Snapchat data'}</div>
            <button style={S.btnOutline} onClick={loadData}>{tr('snapAdsRefresh')}</button>
          </div>
        ) : (
          <>
            {/* ===== EXECUTIVE SUMMARY ===== */}
            <div style={{ display: 'grid', gridTemplateColumns: kpiCols, gap: 16, marginBottom: 24 }}>
              <div style={S.kpi} title={tr('hintSpend')}>
                <div style={{ fontSize: kpiFont, fontWeight: 800, color: t.accent }}>{fmtSAR(totals.spend)}</div>
                <div style={{ fontSize: '0.78rem', color: t.dim, marginTop: 6 }}>{tr('snapAdsTotalSpend')}</div>
              </div>
              <div style={S.kpi} title={tr('hintImpressions')}>
                <div style={{ fontSize: kpiFont, fontWeight: 800 }}>{fmtK(totals.impressions)}</div>
                <div style={{ fontSize: '0.78rem', color: t.dim, marginTop: 6 }}>{tr('snapAdsImpressions')}</div>
              </div>
              <div style={S.kpi} title={tr('hintSwipes')}>
                <div style={{ fontSize: kpiFont, fontWeight: 800 }}>{fmtK(totals.swipes)}</div>
                <div style={{ fontSize: '0.78rem', color: t.dim, marginTop: 6 }}>{tr('snapAdsSwipeUps')}</div>
              </div>
              <div style={S.kpi} title={tr('hintCTR')}>
                <div style={{ fontSize: kpiFont, fontWeight: 800, color: GREEN }}>{fmtP(totals.ctr)}</div>
                <div style={{ fontSize: '0.78rem', color: t.dim, marginTop: 6 }}>{tr('snapAdsCTR')}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: kpiCols, gap: 16, marginBottom: 24 }}>
              <div style={S.kpi} title={tr('hintCPM')}>
                <div style={{ fontSize: kpiFont2, fontWeight: 700 }}>{totals.cpm.toFixed(2)} SAR</div>
                <div style={{ fontSize: '0.78rem', color: t.dim, marginTop: 4 }}>{tr('snapAdsAvgCPM')}</div>
              </div>
              <div style={S.kpi} title={tr('hintCPC')}>
                <div style={{ fontSize: kpiFont2, fontWeight: 700 }}>{totals.cpc.toFixed(2)} SAR</div>
                <div style={{ fontSize: '0.78rem', color: t.dim, marginTop: 4 }}>{tr('snapAdsAvgCPC')}</div>
              </div>
              <div style={S.kpi} title={tr('hintVideoViews')}>
                <div style={{ fontSize: kpiFont2, fontWeight: 700 }}>{fmtK(totals.video_views)}</div>
                <div style={{ fontSize: '0.78rem', color: t.dim, marginTop: 4 }}>{tr('snapAdsVideoViews')}</div>
              </div>
              {accountReach != null && (
                <div style={S.kpi} title={isRTL ? 'عدد الأشخاص الفريدين (دقيق)' : 'Unique people reached (exact)'}>
                  <div style={{ fontSize: kpiFont2, fontWeight: 700 }}>{fmtK(accountReach)}</div>
                  <div style={{ fontSize: '0.78rem', color: t.dim, marginTop: 4 }}>{isRTL ? 'الوصول' : 'Reach'}{accountFreq > 0 ? ` · ${isRTL ? 'التكرار' : 'Freq'} ${accountFreq.toFixed(2)}` : ''}</div>
                </div>
              )}
              <div style={S.kpi}>
                <div style={{ fontSize: kpiFont2, fontWeight: 700 }}>{campaignsWithSpend.length}<span style={{ fontSize: '0.9rem', color: t.dim }}>/{data.campaigns.length}</span></div>
                <div style={{ fontSize: '0.78rem', color: t.dim, marginTop: 4 }}>{isRTL ? 'حملات صرفت في الفترة' : 'Campaigns with spend'}</div>
              </div>
            </div>

            {/* ===== CHARTS ===== */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 20, marginBottom: 24 }}>
              {/* Daily Spend Trend */}
              <div style={S.card}>
                <div style={{ fontWeight: 700, marginBottom: 16, color: t.accent }}>{tr('snapAdsDailySpend')}</div>
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={t.accent} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={t.accent} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={t.gridStroke} />
                      <XAxis dataKey="date" stroke={t.dim} tick={{ fill: t.dim, fontSize: 10 }} />
                      <YAxis stroke={t.dim} tick={{ fill: t.dim, fontSize: 10 }} />
                      <Tooltip content={<TT />} />
                      <Area type="monotone" dataKey="spend" stroke={t.accent} fill="url(#sg)" strokeWidth={2} name="Spend (SAR)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: t.dim }}>{tr('snapAdsNoDailyData')}</div>
                )}
              </div>

              {/* Spend Distribution */}
              <div style={S.card}>
                <div style={{ fontWeight: 700, marginBottom: 16, color: t.accent }}>{tr('snapAdsSpendDist')}</div>
                {spendPie.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={spendPie} cx="50%" cy="50%" outerRadius={90} innerRadius={45} dataKey="value" nameKey="name" paddingAngle={3}>
                        {spendPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip content={<TT />} />
                      <Legend wrapperStyle={{ fontSize: '0.7rem', color: t.dim }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: t.dim }}>{tr('snapAdsNoSpendData')}</div>
                )}
              </div>
            </div>

            {/* ===== CAMPAIGNS TABLE (Executive View) ===== */}
            <div style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: t.accent }}>{tr('snapAdsCampaignsOverview')}</span>
                <span style={{ fontSize: '0.75rem', color: t.dim }}>{tr('snapAdsClickToDrill')}</span>
              </div>
              {/* Search + status filter + sort */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder={isRTL ? 'بحث عن حملة…' : 'Search campaign…'}
                  style={{ ...dateInputStyle, flex: '1 1 180px', minWidth: 140 }} />
                <div style={{ display: 'flex', gap: 4 }}>
                  {[['all', isRTL ? 'الكل' : 'All'], ['active', isRTL ? 'نشطة' : 'Active'], ['paused', isRTL ? 'متوقفة' : 'Paused']].map(([v, lbl]) => (
                    <button key={v} onClick={() => setStatusF(v)} style={{ padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: '0.76rem', fontWeight: statusF === v ? 700 : 500, border: `1px solid ${statusF === v ? t.accent : t.border}`, background: statusF === v ? t.accent : 'transparent', color: statusF === v ? (isDark ? '#000' : '#fff') : t.dim }}>{lbl}</button>
                  ))}
                </div>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...dateInputStyle }}>
                  <option value="spend">{isRTL ? 'ترتيب: الإنفاق' : 'Sort: Spend'}</option>
                  <option value="impressions">{isRTL ? 'ترتيب: الظهور' : 'Sort: Impressions'}</option>
                  <option value="swipes">{isRTL ? 'ترتيب: السحب' : 'Sort: Swipes'}</option>
                </select>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={S.th}>{tr('snapAdsCampaign')}</th>
                      <th style={S.th} title={tr('hintStatus')}>{tr('snapAdsStatus')}</th>
                      <th style={S.th} title={tr('hintObjective')}>{tr('snapAdsObjective')}</th>
                      <th style={S.th} title={tr('hintImpressions')}>{tr('snapAdsImpressions')}</th>
                      <th style={S.th} title={tr('hintSwipes')}>{tr('snapAdsSwipeUps')}</th>
                      <th style={S.th} title={tr('hintSpend')}>{tr('snapAdsSpend')}</th>
                      <th style={S.th} title={tr('hintCTR')}>{tr('snapAdsCTR')}</th>
                      <th style={S.th} title={tr('hintCPM')}>{tr('snapAdsCPM')}</th>
                      <th style={S.th} title={tr('hintCPC')}>{tr('snapAdsCPC')}</th>
                      <th style={S.th} title={tr('hintVideoViews')}>{tr('snapAdsVideoViews')}</th>
                      <th style={S.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.campaigns
                      .filter(c => statusF === 'all' || (statusF === 'active' ? c.status === 'ACTIVE' : c.status !== 'ACTIVE'))
                      .filter(c => !search.trim() || (c.name || '').toLowerCase().includes(search.trim().toLowerCase()))
                      .slice().sort((a, b) => (b.metrics?.[sortBy] || 0) - (a.metrics?.[sortBy] || 0)).map((c, i) => {
                      const m = c.metrics || {};
                      const isSelected = expandedCampaign === c.campaign_id;
                      return (
                        <tr key={c.campaign_id}
                          onClick={() => setExpandedCampaign(isSelected ? null : c.campaign_id)}
                          style={{
                            cursor: 'pointer',
                            background: isSelected ? t.adsetBg : (i % 2 ? t.rowAlt : 'transparent'),
                            borderRight: isSelected ? `3px solid ${t.accent}` : '3px solid transparent',
                          }}>
                          <td style={{ ...S.td, fontWeight: 600, maxWidth: 200 }}>{c.name}</td>
                          <td style={S.td}><StatusTag status={c.status} t={t} S={S} /></td>
                          <td style={S.td}><span style={{ ...S.tag, background: t.objTagBg, color: t.objTagColor, fontSize: '0.65rem' }}>{c.objective}</span></td>
                          <td style={{ ...S.td, fontWeight: 600 }}>{fmtK(m.impressions)}</td>
                          <td style={S.td}>{fmtK(m.swipes)}</td>
                          <td style={{ ...S.td, color: t.accent, fontWeight: 700 }}>{m.spend?.toFixed(2) || '0.00'}</td>
                          <td style={{ ...S.td, color: GREEN }}>{fmtP(m.ctr)}</td>
                          <td style={S.td}>{m.cpm?.toFixed(2) || '—'}</td>
                          <td style={S.td}>{m.cpc?.toFixed(2) || '—'}</td>
                          <td style={S.td}>{fmtK(m.video_views)}</td>
                          <td style={{ ...S.td, color: t.accent }}>{isSelected ? '▲' : '▼'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ===== CAMPAIGN DRILL-DOWN ===== */}
            {selectedCampaign && (
              <div style={{ ...S.card, borderColor: t.accent, borderWidth: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <div>
                    <span style={{ fontSize: '1.1rem', fontWeight: 800, color: t.accent }}>{selectedCampaign.name}</span>
                    <StatusTag status={selectedCampaign.status} t={t} S={S} />
                    <span style={{ ...S.tag, background: t.objTagBg, color: t.objTagColor }}>{selectedCampaign.objective}</span>
                  </div>
                  <button style={S.btnSmall} onClick={() => setExpandedCampaign(null)}>{tr('close')}</button>
                </div>

                {/* Campaign info row */}
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 20, fontSize: '0.82rem', color: t.dim }}>
                  <span>{tr('snapAdsStart')} {selectedCampaign.start_time?.slice(0, 10) || '\u2014'}</span>
                  <span>{tr('snapAdsEnd')} {selectedCampaign.end_time?.slice(0, 10) || tr('snapAdsOngoing')}</span>
                  {selectedCampaign.daily_budget > 0 && <span>{tr('snapAdsBudget')} {selectedCampaign.daily_budget.toFixed(0)} SAR/day</span>}
                </div>

                {/* Campaign KPIs */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)', gap: 12, marginBottom: 20 }}>
                  {[
                    { label: tr('snapAdsImpressions'), value: fmtK(selectedCampaign.metrics?.impressions), hint: tr('hintImpressions') },
                    { label: tr('snapAdsSwipeUps'), value: fmtK(selectedCampaign.metrics?.swipes), hint: tr('hintSwipes') },
                    { label: tr('spend'), value: fmtSAR(selectedCampaign.metrics?.spend), gold: true, hint: tr('hintSpend') },
                    { label: tr('snapAdsCTR'), value: fmtP(selectedCampaign.metrics?.ctr), hint: tr('hintCTR') },
                    { label: isRTL ? '\u0627\u0644\u0648\u0635\u0648\u0644' : 'Reach', value: selectedCampaign.metrics?.reach ? fmtK(selectedCampaign.metrics.reach) : '\u2014', hint: isRTL ? '\u0639\u062f\u062f \u0627\u0644\u0623\u0634\u062e\u0627\u0635 \u0627\u0644\u0641\u0631\u064a\u062f\u064a\u0646' : 'Unique people reached' },
                    { label: tr('snapAdsFrequency'), value: selectedCampaign.metrics?.frequency ? selectedCampaign.metrics.frequency.toFixed(2) : '\u2014', hint: tr('hintFreq') },
                  ].map((kpi, i) => (
                    <div key={i} style={{ textAlign: 'center', padding: 12, background: t.adsetBg, borderRadius: 10 }} title={kpi.hint || ''}>
                      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: kpi.gold ? t.accent : t.text }}>{kpi.value}</div>
                      <div style={{ fontSize: '0.7rem', color: t.dim, marginTop: 4 }}>{kpi.label}</div>
                    </div>
                  ))}
                </div>

                {/* Ad Squads */}
                {(selectedCampaign.adsets || []).length > 0 && (
                  <>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: t.accent, marginBottom: 12 }}>
                      {tr('snapAdsAdSquads')} ({selectedCampaign.adsets.length})
                    </div>
                    {selectedCampaign.adsets.map(sq => (
                      <div key={sq.adsquad_id} style={{ background: t.adsetBg, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16, marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                          <div>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{sq.name}</span>
                            <StatusTag status={sq.status} t={t} S={S} />
                          </div>
                          <div style={{ display: 'flex', gap: 12, fontSize: '0.75rem', color: t.dim }}>
                            {sq.daily_budget > 0 && <span>Budget: {sq.daily_budget.toFixed(0)} SAR/day</span>}
                            {sq.targeting && (
                              <span>
                                {sq.targeting.age_min || sq.targeting.age_max ? `Age: ${sq.targeting.age_min || '?'}-${sq.targeting.age_max || '?'}` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Squad metrics inline */}
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)', gap: 8 }}>
                          {[
                            { label: tr('snapAdsImpressions'), value: fmtK(sq.metrics?.impressions), hint: tr('hintImpressions') },
                            { label: tr('snapAdsSwipeUps'), value: fmt(sq.metrics?.swipes), hint: tr('hintSwipes') },
                            { label: tr('spend'), value: sq.metrics?.spend?.toFixed(2) || '0', gold: true, hint: tr('hintSpend') },
                            { label: tr('snapAdsCTR'), value: fmtP(sq.metrics?.ctr), hint: tr('hintCTR') },
                            { label: tr('snapAdsCPM'), value: sq.metrics?.cpm?.toFixed(2) || '—', hint: tr('hintCPM') },
                            { label: tr('snapAdsCPC'), value: sq.metrics?.cpc?.toFixed(2) || '—', hint: tr('hintCPC') },
                          ].map((m, i) => (
                            <div key={i} style={{ textAlign: 'center', padding: '4px 2px' }} title={m.hint || ''}>
                              <div style={{ fontSize: '0.6rem', color: t.dim, marginBottom: 2 }}>{m.label}</div>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: m.gold ? t.accent : t.text }}>{m.value}</div>
                            </div>
                          ))}
                        </div>

                        {/* Ads in this squad */}
                        {(sq.ads || []).length > 0 && (
                          <div style={{ marginTop: 10, overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th style={{ ...S.th, fontSize: '0.65rem', width: 52 }}></th>
                                  <th style={{ ...S.th, fontSize: '0.65rem' }}>Ad</th>
                                  <th style={{ ...S.th, fontSize: '0.65rem' }} title={tr('hintStatus')}>{tr('snapAdsStatus')}</th>
                                  <th style={{ ...S.th, fontSize: '0.65rem' }}>{tr('snapHistType')}</th>
                                  <th style={{ ...S.th, fontSize: '0.65rem' }} title={tr('hintImpressions')}>{tr('snapAdsImpressions')}</th>
                                  <th style={{ ...S.th, fontSize: '0.65rem' }} title={tr('hintSwipes')}>{tr('snapAdsSwipeUps')}</th>
                                  <th style={{ ...S.th, fontSize: '0.65rem' }} title={tr('hintSpend')}>{tr('spend')}</th>
                                  <th style={{ ...S.th, fontSize: '0.65rem' }} title={tr('hintCTR')}>{tr('snapAdsCTR')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sq.ads.map((ad, ai) => (
                                  <tr key={ad.ad_id} style={ai % 2 ? { background: t.rowAlt } : {}}>
                                    <td style={{ ...S.td, padding: '6px 8px' }}>
                                      {ad.thumbnail_url
                                        ? <img src={ad.thumbnail_url} alt="" loading="lazy" className="cmp-thumb"
                                            onClick={() => setCreativePreview(ad)}
                                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: `1px solid ${t.border}` }} />
                                        : <div style={{ width: 40, height: 40, borderRadius: 8, background: 'linear-gradient(135deg,#f7b500,#ff6b00)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>👻</div>}
                                    </td>
                                    <td style={{ ...S.td, fontSize: '0.8rem', fontWeight: 600, maxWidth: 180 }}>{ad.name || '—'}{data.adLevelLifetime && <span title={isRTL ? 'أرقام الإعلان إجمالية طوال العمر وليست للفترة المحددة' : 'Ad figures are lifetime totals, not the selected range'} style={{ marginInlineStart: 6, fontSize: '0.54rem', fontWeight: 700, padding: '1px 5px', borderRadius: 5, background: t.inputBg, color: t.dim, border: `1px solid ${t.border}` }}>{isRTL ? 'إجمالي' : 'lifetime'}</span>}</td>
                                    <td style={S.td}><StatusTag status={ad.status} t={t} S={S} /></td>
                                    <td style={{ ...S.td, fontSize: '0.75rem', color: t.dim }}>{ad.creative_type || '—'}</td>
                                    <td style={S.td}>{fmtK(ad.metrics?.impressions)}</td>
                                    <td style={S.td}>{fmt(ad.metrics?.swipes)}</td>
                                    <td style={{ ...S.td, color: t.accent, fontWeight: 600 }}>{ad.metrics?.spend?.toFixed(2) || '0'}</td>
                                    <td style={{ ...S.td, color: GREEN }}>{fmtP(ad.metrics?.ctr)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Creative + detail modal — click a thumbnail to see the ad, its details, and edit it */}
      {creativePreview && (
        <AdDetailModal ad={creativePreview} platform="snapchat"
          onClose={() => setCreativePreview(null)} onChanged={() => loadData()} />
      )}
    </div>
  );
}

export default SnapAds;
